import { gql } from '@apollo/client';
import dayjs from 'dayjs';
import ical from 'ical-generator';
import ReactDOMServer from 'react-dom/server';

import { InPersonAddressFragment } from '@/fragments/venue-fragment';

import { UseAddToCalendarLink_EventFragment } from './graphql-types/UseAddToCalendarLink_EventFragment';

import { Description } from './components/Description';
import { buildURI } from './utils';

export const useAddToCalendarLink = (
  communityName: string,
  hostnameAndProtocol: string,
  eventDataRaw: UseAddToCalendarLink_EventFragment
): {
  calendarLinkSpecificForGoogle: string;
  calendarLink: string;
} => {
  const hostname = hostnameAndProtocol
    .replace(/^.+:\/\//, '')
    .replace(/\//g, '');

  const url = `${hostnameAndProtocol}events/${eventDataRaw._id}`;

  /**
   * Prioritize setting the `location` of the calendar event to the TW event's in person
   * location, if one exists. Otherwise, set it to the TW event details page URL.
   */
  const location = (() => {
    const inPersonAddress =
      eventDataRaw.__typename === 'ConferenceEventV2'
        ? eventDataRaw.inPersonLocation?.address
        : eventDataRaw.__typename === 'SimpleEventV2'
        ? eventDataRaw.venue?.inPersonLocation?.address
        : null;

    const addressWithoutName = inPersonAddress
      ? [
          inPersonAddress.streetAddress,
          inPersonAddress.streetAddressLine2,
          inPersonAddress.locality,
          inPersonAddress.administrativeArea,
          inPersonAddress.country,
          inPersonAddress.postalCode,
        ]
          .filter((value) => value)
          .join(', ')
      : null;

    return addressWithoutName && addressWithoutName?.length > 0
      ? addressWithoutName
      : url;
  })();

  const htmlDescription = ReactDOMServer.renderToStaticMarkup(
    <Description event={eventDataRaw} isHtml />
  );

  const textDescription = ReactDOMServer.renderToStaticMarkup(
    <Description event={eventDataRaw} isHtml={false} />
  )
    .replace(/(<br[^\/]*\/>)/gi, '\n')
    .replace(/(<([^>]+)>)/gi, '');

  const icalString = ical({
    domain: hostname,
    prodId: `//${communityName}//${eventDataRaw.name}//EN`,
    events: [
      {
        uid: eventDataRaw._id,
        start: new Date(eventDataRaw.startDate),
        end: new Date(eventDataRaw.endDate),
        summary: eventDataRaw.name,
        url,
        description: textDescription,
        htmlDescription,
        location,
      },
    ],
  }).toString();

  /**
   * Add the URL to the end of the Google Calendar description
   * if it is not already set in the `location` field
   */
  const eventURLSuffix = url === location ? '' : `\n\n${url}`;

  const dateToGoogleISO = (dateString: string): string =>
    dateString.replace(/[-:]/g, '');
  const googleLinkUrlParametersObject = {
    text: eventDataRaw.name,
    dates: `${dateToGoogleISO(
      dayjs(eventDataRaw.startDate).format()
    )}/${dateToGoogleISO(dayjs(eventDataRaw.endDate).format())}`,
    details:
      textDescription?.length > 1000
        ? textDescription?.substring(0, 1000) + '...' + eventURLSuffix
        : textDescription + eventURLSuffix,
    location,
    sf: true,
  };
  const googleLinkParameters = Object.entries(googleLinkUrlParametersObject)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');

  return {
    calendarLinkSpecificForGoogle: `https://calendar.google.com/calendar/u/0/r/eventedit?${googleLinkParameters}`,
    calendarLink: buildURI(icalString, false),
  };
};

const EVENT_ORGANIZATION_FRAGMENT = gql`
  fragment UseAddToCalendarLink_OrganizationFragment on Organization {
    _id
    displayName
    profilePicture {
      _id
      imageProxy(input: { w: 448, h: 448, fit: "crop" })
    }
  }
`;

const EVENT_SPONSORSHIP_FRAGMENT = gql`
  fragment UseAddToCalendarLink_ConferenceEvent_Sponsors on ConferenceEvent_Sponsors {
    firstTierSponsors {
      _id
      ...UseAddToCalendarLink_OrganizationFragment
    }
    secondTierSponsors {
      _id
      ...UseAddToCalendarLink_OrganizationFragment
    }
    thirdTierSponsors {
      _id
      ...UseAddToCalendarLink_OrganizationFragment
    }
  }
  ${EVENT_ORGANIZATION_FRAGMENT}
`;

const SESSIONS_FRAGMENT = gql`
  fragment UseAddToCalendarLink_ConferenceSessionsFragment on ConferenceSession {
    _id
    title
    description
    image {
      _id
      imageProxy(input: { h: 169, w: 404, fit: "crop" })
    }
    speakers {
      user {
        _id
        displayName
        jobTitle
        company
      }
      isHidden
    }
    startDate
    endDate

    sponsors {
      keySponsor {
        ... on Organization {
          _id
        }
      }
    }
    isCurrentUserSpeaker
  }
`;

useAddToCalendarLink.eventFragment = gql`
  fragment UseAddToCalendarLink_EventFragment on EventV2 {
    _id
    startDate
    endDate
    ...Description_EventFragment
    ... on ConferenceEventV2 {
      _id
      conferenceSponsors {
        ...UseAddToCalendarLink_ConferenceEvent_Sponsors
      }
      sponsorshipTierNames {
        firstTier
        secondTier
        thirdTier
      }
      sessions(input: $conferenceSessionsInput) {
        results {
          _id
          ...UseAddToCalendarLink_ConferenceSessionsFragment
        }
      }
      inPersonLocation {
        address {
          ...InPersonAddressFragment
        }
      }
    }
    ... on SimpleEventV2 {
      _id
      simpleEventSponsors {
        keySponsor {
          ... on Organization {
            _id
            ...UseAddToCalendarLink_OrganizationFragment
          }
        }
        additionalSponsors {
          ... on Organization {
            _id
            ...UseAddToCalendarLink_OrganizationFragment
          }
        }
      }
      speakers {
        user {
          _id
          displayName
          jobTitle
          company
        }
        isHidden
      }
      venue {
        inPersonLocation {
          address {
            ...InPersonAddressFragment
          }
        }
      }
    }
  }
  ${Description.eventFragment}
  ${EVENT_SPONSORSHIP_FRAGMENT}
  ${EVENT_ORGANIZATION_FRAGMENT}
  ${SESSIONS_FRAGMENT}
  ${InPersonAddressFragment}
`;
